"use client";
import { jsx } from "react/jsx-runtime";
import { MarkdownRenderer } from "@nxt-costco-com/forge-components";
import { FontWeightDefault } from "@nxt-costco-com/forge-design-tokens";
import { MarkdownExternalLinkHandler } from "./MarkdownLinkOverride/index.js";
import { MarkdownTooltipOverride } from "./MarkdownTooltipOverride/index.js";
import { MarkdownVideoLinkOverride } from "./MarkdownVideoLinkOverride/index.js";
import { COMPONENT_NAME } from "./constants.js";
import { CustomMarketingTypography } from "./styles.js";
const MarketingTypography = ({
  variant = "bodycopy",
  children,
  color,
  isMarkdown = false,
  uniqueId = COMPONENT_NAME,
  sx,
  italic,
  underline,
  id,
  tabIndex,
  role,
  component,
  className,
  isCustomParsingEnabled = false,
  tooltipStyles = {},
  isDisableLinks = false
}) => {
  const baseSx = [{ color }, ...Array.isArray(sx) ? sx : [sx]];
  const customSx = isMarkdown ? [{ fontWeight: FontWeightDefault }, ...baseSx] : [...baseSx];
  const LinkOverride = ({
    children: children2
  }) => children2;
  const preprocessTooltips = (markdown) => {
    const MAX_MARKDOWN_LENGTH = 1e3;
    if (markdown.length > MAX_MARKDOWN_LENGTH) {
      console.warn(
        `[MarketingTypography] Markdown input too long (${markdown.length} chars), skipping tooltip preprocessing`
      );
      return {
        processedMarkdown: markdown,
        TooltipComponent: MarkdownTooltipOverride
      };
    }
    const tooltipRegex = /<Tooltip\s+content="((?:[^"\\]|\\.)*?)"\s+([^>]*?)>/g;
    const contents = [];
    let processed = markdown;
    let match;
    while ((match = tooltipRegex.exec(markdown)) !== null) {
      const [fullMatch, content2, otherAttrs] = match;
      contents.push(content2);
      processed = processed.replace(
        fullMatch,
        `<Tooltip data-index="${contents.length - 1}" ${otherAttrs}>`
      );
    }
    const TooltipComponent2 = (props) => {
      const index = props["data-index"] !== void 0 ? Number(props["data-index"]) : void 0;
      const actualContent = index !== void 0 ? contents[index] : props.content;
      return /* @__PURE__ */ jsx(
        MarkdownTooltipOverride,
        {
          ...props,
          content: actualContent,
          tooltipStyles
        }
      );
    };
    return { processedMarkdown: processed, TooltipComponent: TooltipComponent2 };
  };
  const { processedMarkdown, TooltipComponent } = preprocessTooltips(
    children ?? ""
  );
  const content = isMarkdown ? isCustomParsingEnabled ? /* @__PURE__ */ jsx(
    MarkdownRenderer,
    {
      linkColor: color,
      markdown: processedMarkdown,
      overrides: {
        a: isDisableLinks ? LinkOverride : MarkdownExternalLinkHandler,
        Tooltip: {
          component: TooltipComponent
        },
        VideoLink: MarkdownVideoLinkOverride,
        p: {
          component: "div"
        }
      }
    }
  ) : /* @__PURE__ */ jsx(
    MarkdownRenderer,
    {
      linkColor: color,
      markdown: children,
      overrides: {
        a: isDisableLinks ? LinkOverride : MarkdownExternalLinkHandler
      }
    }
  ) : children;
  const standardizedVariantMap = {
    body: "bodycopy",
    body_v2: "bodycopy_v2",
    bodycopy: "bodycopy",
    bodycopy_v2: "bodycopy_v2",
    body_copy_v2: "bodycopy_v2",
    body_copy_sm_v2: "body_copy_sm_v2",
    caption: "caption",
    caption_v2: "caption_v2",
    disclaimer: "caption",
    disclaimer_v2: "caption_v2",
    heading: "heading",
    heading_v2: "heading_v2",
    subheading: "subheading",
    subheading_v2: "subheading_v2",
    subtitle: "subheading",
    subtitle_v2: "subheading_v2",
    title: "title",
    title_v2: "title_v2",
    title_with_xl: "title_with_xl",
    title_with_xl_v2: "title_with_xl_v2"
  };
  return /* @__PURE__ */ jsx(
    CustomMarketingTypography,
    {
      className,
      component,
      id,
      italic,
      role,
      sx: customSx,
      tabIndex,
      underline,
      uniqueId,
      variant: standardizedVariantMap[variant] || "bodycopy",
      children: content
    }
  );
};
MarketingTypography.displayName = COMPONENT_NAME;
export {
  MarketingTypography
};
